import {
  DeepPartial,
  TellescopeModelFromName,
  useAuth,
  useDebouncedMutation,
  usePermissionQuery,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
  useUsersMap,
} from "@tellescope/tanstack-query";
import {
  Accordion,
  Button,
  Label,
  MultiSelect,
  MultiSelectOption,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  useDateFnsFormattersWithLocale,
} from "@tellescope/ui";
import { Calendar, Plus, Share2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getDaysOfWeekFull } from "../constants";
import { WeeklyAvailability } from "@tellescope/types-models";
import { DayAvailabilityAccordionItem } from "./DayAvailabilityAccordionItem";
import { LimitBlock } from "./LimitBlock";
import { formatName } from "@tellescope/utils";

export interface UniqueWeeklyAvailability extends WeeklyAvailability {
  _originalIndex: number;
}

export interface AvailabilityViewProps {
  userIds: string[];
  onCopyError?: (userIds: string[]) => void;
  onCopySuccess?: (userIds: string[]) => void;
}

type UserModel = TellescopeModelFromName<"users">;

const AVAILABILITY_DEBOUNCE_MS = 1500;

export const AvailabilityView = ({ userIds, onCopyError, onCopySuccess }: AvailabilityViewProps) => {
  const { t } = useTranslation("calendar");
  const { currentUser, isAdmin } = useAuth();
  const { formatInTimeZone } = useDateFnsFormattersWithLocale();
  const userMap = useUsersMap();
  const userToDisplay = useMemo(
    () => (userIds.length === 1 ? userMap[userIds[0] ?? ""] : undefined),
    [userIds, userMap]
  );
  const [usersToCopyTo, setUsersToCopyTo] = useState<string[]>([]);

  const updateUser = useTellescopeMutation({
    model: "users",
    method: "updateOne",
    optimisticUpdates: true,
  });

  const {
    value: displayUser,
    apply: applyDisplayUserChanges,
    flush: flushDisplayUserChanges,
  } = useDebouncedMutation<UserModel>({
    baseValue: userToDisplay,
    storageKey: userToDisplay?.id ? `availability-pending-${userToDisplay.id}` : undefined,
    debounceMs: AVAILABILITY_DEBOUNCE_MS,
    replaceObjectFields: true,
    commit: async (changes: DeepPartial<UserModel>) => {
      if (!userToDisplay?.id) return;
      await updateUser.mutateAsync({
        id: userToDisplay.id,
        ...changes,
        _options: { replaceObjectFields: true },
      });
    },
  });

  const timezone = displayUser?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const {
    update: { access: canUpdate },
  } = usePermissionQuery({
    model: "users",
    actions: ["update"],
    record: userToDisplay,
  });
  const {
    update: { access: canUpdateSelf },
  } = usePermissionQuery({
    model: "users",
    actions: ["update"],
    record: currentUser,
  });
  const canCopyToSelf = useMemo(() => {
    if (!canUpdateSelf) return false;
    if (currentUser.id === userToDisplay?.id) return false;
    return true;
  }, [canUpdateSelf, currentUser.id, userToDisplay?.id]);
  const canCopyToOthers = isAdmin;
  const showCopyButton = canCopyToSelf || canCopyToOthers;
  const userCopyOptions = useMemo<MultiSelectOption[]>(() => {
    if (canCopyToOthers) {
      return Object.keys(userMap).reduce<MultiSelectOption[]>((acc, id) => {
        const user = userMap[id];
        if (id !== userToDisplay?.id && user) {
          acc.push({
            label: formatName(user) || t("User: {{id}}", { id: user.id }),
            value: id,
          });
        }
        return acc;
      }, []);
    }

    if (userToDisplay?.id !== currentUser.id) {
      setUsersToCopyTo([currentUser.id]);
      return [{ label: formatName(currentUser), value: currentUser.id }];
    }

    return [];
  }, [canCopyToOthers, currentUser, t, userMap, userToDisplay?.id]);
  const [isCopying, setIsCopying] = useState(false);
  const [copyPopoverOpen, setCopyPopoverOpen] = useState(false);

  const formatTimeFromMinutes = useCallback(
    (minutes: number) =>
      formatInTimeZone(new Date(Date.UTC(2000, 0, 1, Math.floor(minutes / 60), minutes % 60)), "UTC", "h:mm a"),
    [formatInTimeZone]
  );

  const timezoneAbbreviation = useMemo(
    () => formatInTimeZone(new Date(), timezone, "zzz"),
    [formatInTimeZone, timezone]
  );

  const availabilityBlocksByDay = useMemo(
    () =>
      displayUser?.weeklyAvailabilities?.reduce<Record<number, UniqueWeeklyAvailability[]>>((acc, curr, i) => {
        const updated = acc[curr.dayOfWeekStartingSundayIndexedByZero] ?? [];
        updated.push({
          ...curr,
          _originalIndex: i,
        });
        acc[curr.dayOfWeekStartingSundayIndexedByZero] = updated;
        return acc;
      }, {}) ?? {},
    [displayUser?.weeklyAvailabilities]
  );

  const handleAddBlock = useCallback(
    (dayIndex: number) => {
      const latestBlockEnd = [...(availabilityBlocksByDay[dayIndex] ?? [])].sort(
        (a, b) => b.endTimeInMinutes - a.endTimeInMinutes
      )?.[0]?.endTimeInMinutes;
      const DEFAULT_BLOCK: WeeklyAvailability = {
        dayOfWeekStartingSundayIndexedByZero: dayIndex,
        startTimeInMinutes: latestBlockEnd ?? 9 * 60,
        endTimeInMinutes: latestBlockEnd ? latestBlockEnd + 60 : (9 + 8) * 60,
      };

      applyDisplayUserChanges({
        weeklyAvailabilities: [...(displayUser?.weeklyAvailabilities ?? []), DEFAULT_BLOCK],
      });
    },
    [applyDisplayUserChanges, availabilityBlocksByDay, displayUser?.weeklyAvailabilities]
  );

  const handleBlockChange = useCallback(
    ({ _originalIndex, ...newBlock }: UniqueWeeklyAvailability, isDelete?: boolean) => {
      const originalBlocks = displayUser?.weeklyAvailabilities ?? [];
      const newBlocks = isDelete
        ? [...originalBlocks.slice(0, _originalIndex), ...originalBlocks.slice(_originalIndex + 1)]
        : [...originalBlocks.slice(0, _originalIndex), newBlock, ...originalBlocks.slice(_originalIndex + 1)];

      applyDisplayUserChanges({ weeklyAvailabilities: newBlocks });
    },
    [applyDisplayUserChanges, displayUser?.weeklyAvailabilities]
  );

  const handleBlockDelete = useCallback(
    (index: number) => {
      const originalBlocks = displayUser?.weeklyAvailabilities ?? [];
      applyDisplayUserChanges({
        weeklyAvailabilities: [...originalBlocks.slice(0, index), ...originalBlocks.slice(index + 1)],
      });
    },
    [applyDisplayUserChanges, displayUser?.weeklyAvailabilities]
  );

  const handleCopy = useCallback(
    async (targetUserIds: string[]) => {
      if (!displayUser) return;

      setIsCopying(true);
      try {
        const sourcePayload = {
          weeklyAvailabilities: displayUser.weeklyAvailabilities,
          autoReplyEnabled: displayUser.autoReplyEnabled,
          calendarEventLimits: displayUser.calendarEventLimits,
        };
        // The flush can reject if the source user's debounced write fails. Swallow
        // it — the failure is already surfaced via the debounced-mutation hook's
        // error state, and the copy targets are independent updates that should
        // still complete their success/error callbacks.
        const flushPromise = flushDisplayUserChanges().catch(() => undefined);

        const res = await Promise.allSettled(
          targetUserIds.map(async (id) =>
            updateUser.mutateAsync({
              id,
              ...sourcePayload,
              _options: { replaceObjectFields: true },
            })
          )
        );

        await flushPromise;

        const usersWithSuccess = res.reduce<Set<string>>((acc, curr) => {
          if (curr.status === "fulfilled") acc.add(curr.value.id);
          return acc;
        }, new Set<string>());
        if (usersWithSuccess.size) onCopySuccess?.(Array.from(usersWithSuccess));
        if (usersWithSuccess.size !== targetUserIds.length) {
          onCopyError?.(targetUserIds.filter((id) => !usersWithSuccess.has(id)));
        }
        setUsersToCopyTo([]);
        setCopyPopoverOpen(false);
      } finally {
        setIsCopying(false);
      }
    },
    [displayUser, flushDisplayUserChanges, onCopyError, onCopySuccess, updateUser]
  );

  const { data: defaultTemplateId } = useTellescopeInfiniteQuery({
    model: "calendar_event_templates",
    method: "getSome",
    request: { limit: 1 },
    select: (data) => data.pages.flat().at(0)?.id,
  });

  return (
    <>
      <section data-testid="availability-page" className="shrink-0 p-6 space-y-2 border-b">
        <h1 className="text-3xl font-semibold">{t("Availability")}</h1>
        <h3 className="font-light text-muted-foreground" data-testid="availability-summary">
          {displayUser
            ? t("{{count}} availability blocks", { count: displayUser.weeklyAvailabilities?.length ?? 0 })
            : t("Select a user to view availability")}
        </h3>
      </section>
      <section className="overflow-y-auto">
        {displayUser ? (
          <div data-testid="availability-content" className="p-6 space-y-6">
            <div className="border rounded-sm p-4 flex items-center gap-4">
              <span className="flex items-center gap-2">
                <Switch
                  checked={!!displayUser.autoReplyEnabled}
                  id="auto-reply-switch"
                  data-testid="availability-auto-reply-switch"
                  onCheckedChange={(autoReplyEnabled) => applyDisplayUserChanges({ autoReplyEnabled })}
                  disabled={!canUpdate}
                />
                <Label htmlFor="auto-reply-switch" className="text-xs">
                  {t("Auto-reply")}
                </Label>
              </span>
              {showCopyButton && (
                <Popover open={copyPopoverOpen} onOpenChange={setCopyPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-auto py-1 text-xs font-light"
                      data-testid="availability-copy-trigger"
                    >
                      <Share2 className="size-3.5" />
                      {t("Copy to user")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-3 space-y-2">
                    <p className="text-sm font-medium">{t("Copy availability to")}</p>
                    <p className="text-xs font-light text-muted-foreground">
                      {t("All blocks will be copied to the selected users.")}
                    </p>
                    <MultiSelect
                      value={usersToCopyTo}
                      options={userCopyOptions}
                      onValueChange={setUsersToCopyTo}
                      placeholder={t("Select users...")}
                      className="font-normal w-full"
                    />
                    <Button
                      className="w-full font-light text-xs h-auto py-1.5"
                      size="sm"
                      disabled={!usersToCopyTo.length}
                      loading={isCopying}
                      onClick={() => handleCopy(usersToCopyTo)}
                    >
                      {t("Copy to {{count}} users", { count: usersToCopyTo.length })}
                    </Button>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between w-full">
                <h3 className="text-sm font-semibold">{t("Weekly Availability")}</h3>
                <p className="text-muted-foreground text-xs">
                  {t("{{count}} blocks total", { count: displayUser.weeklyAvailabilities?.length ?? 0 })}
                </p>
              </div>
              <Accordion type="multiple" className="space-y-2">
                {getDaysOfWeekFull().map((day, i) => (
                  <DayAvailabilityAccordionItem
                    key={day}
                    day={day}
                    dayIndex={i}
                    blocks={availabilityBlocksByDay[i] ?? []}
                    timezoneAbbreviation={timezoneAbbreviation}
                    formatTimeFromMinutes={formatTimeFromMinutes}
                    onAddBlock={handleAddBlock}
                    onBlockChange={(block) => handleBlockChange(block)}
                    onBlockDelete={handleBlockDelete}
                    canUpdate={canUpdate}
                  />
                ))}
              </Accordion>
            </div>
            <div className="border-t py-6 space-y-2">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold">{t("Appointment Limits")}</h3>
                <p className="text-xs text-muted-foreground font-light">
                  {t("Cap bookings per event type over a rolling period.")}
                </p>
              </div>
              {displayUser.calendarEventLimits?.map((limit, i) => (
                <LimitBlock
                  key={`limit-${i}`}
                  limit={limit}
                  onChange={(updatedLimit) => {
                    const limits = displayUser.calendarEventLimits ?? [];
                    applyDisplayUserChanges({
                      calendarEventLimits: [...limits.slice(0, i), updatedLimit, ...limits.slice(i + 1)],
                    });
                  }}
                  onDelete={() => {
                    const limits = displayUser.calendarEventLimits ?? [];
                    applyDisplayUserChanges({
                      calendarEventLimits: [...limits.slice(0, i), ...limits.slice(i + 1)],
                    });
                  }}
                  canUpdate={canUpdate}
                />
              ))}
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-auto py-1 px-1.5 font-light"
                disabled={!defaultTemplateId || !canUpdate}
                onClick={() => {
                  applyDisplayUserChanges({
                    calendarEventLimits: [
                      ...(displayUser.calendarEventLimits ?? []),
                      {
                        limit: 1,
                        period: 1,
                        templateId: defaultTemplateId ?? "",
                        otherTemplateIds: [],
                      },
                    ],
                  });
                }}
              >
                <Plus className="size-3" />
                {t("Add limit")}
              </Button>
            </div>
          </div>
        ) : (
          <div
            data-testid={userIds.length ? "availability-empty-multiple-users" : "availability-empty-no-user"}
            className="py-[120px] flex flex-col gap-10 items-center"
          >
            <Calendar className="text-muted-foreground" size={16} />
            <div className="flex flex-col gap-2 text-sm items-center max-w-[250px] text-center font-light">
              <p className="font-medium">{userIds.length ? t("Multiple Users Selected") : t("No User Selected")}</p>
              <p className="text-muted-foreground">
                {userIds.length
                  ? t(
                      "Please select only one user to view and edit availability. Multiple user editing is not supported."
                    )
                  : t("Select a user from the header to view and edit their availability settings.")}
              </p>
            </div>
          </div>
        )}
      </section>
    </>
  );
};
